import { styled, Dialog, AlertColor, Alert } from "@mui/material"
import ScheduleMeetingDialogConfirmDate from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogConfirmDate"
import ScheduleMeetingDialogReviewDate from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogReviewDate"
import { ScheduleMeetingDialogSelectTime } from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogSelectTime"
import ScheduleMeetingDialogSelectType from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogSelectType"
import deleteConventionMeetingRegistration from "lib/deleteConventionMeetingRegistration"
import postConventionMeetingRegistration from "lib/postConventionMeetingRegistration"
import useCurrent from "lib/use-current"
import ConventionAvailableMeetingTime from "models/convention-api/v1/ConventionAvailableMeetingTime"
import ConventionMeetingAvailability from "models/convention-api/v1/ConventionMeetingAvailability"
import ConventionMeetingType from "models/convention-api/v1/ConventionMeetingType"
import moment from "moment-timezone"
import { useSnackbar } from "notistack"
import React, { useMemo, useState } from "react"

enum ScheduleMeetingSteps {
  SELECT_TYPE,
  SELECT_TIME,
  CONFIRM_DATE,
  REVIEW_DATE,
}

interface ScheduleMeetingModalProps {
  institutionId: string
  onClose: () => void
  institutionName: string
}

const ScheduleMeetingDialog = (props: ScheduleMeetingModalProps) => {
  const { institutionId, onClose, institutionName } = props
  const current = useCurrent()

  const [scheduleMeetingStep, setScheduleMeetingStep] = useState<ScheduleMeetingSteps>(
    // The default one
    ScheduleMeetingSteps.SELECT_TYPE,
  )
  const [isLoading, setIsLoading] = useState(false)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const [selectedMeetingType, setSelectedMeetingType] = useState<ConventionMeetingType | null>(null)
  const [selectedMeetingTime, setSelectedMeetingTime] =
    useState<ConventionAvailableMeetingTime | null>(null)
  const [lastSelectedMeetingId, setLastSelectedMeetingId] = useState<string | null>(null)

  const meetingTypes = ConventionMeetingType.useAll({
    filter: {
      institution_id: institutionId,
    },
  })

  const minAvailableTimeFilter = useMemo(() => {
    const minAvailableTime = moment().add(15, "minutes")
    return moment()
      .add(getNearest5MinuteInterval(minAvailableTime.minute()), "minutes")
      .startOf("minutes")
      .toISOString()
  }, [])

  const meetingAvailabilities = ConventionMeetingAvailability.useAll(() => {
    if (!meetingTypes.data) return null

    return {
      filter: {
        end_at_gteq: minAvailableTimeFilter,
        convention_meeting_type_id: meetingTypes.data.map((meetingType) => meetingType.id),
      },
    }
  })

  const meetingTimes = ConventionAvailableMeetingTime.useAll(() => {
    if (!meetingTypes) return null
    if (!selectedMeetingType) return null

    return {
      filter: {
        end_at_gteq: minAvailableTimeFilter,
        institution_id: institutionId,
        convention_meeting_type_id: selectedMeetingType.id,
      },
      includes: ["meetingAvailability", "meetingAvailability.adminUser"],
    }
  })

  const showSnackbar = (message: string, severity: AlertColor = "info") => {
    enqueueSnackbar(message, {
      autoHideDuration: 2000,
      content: (key) => (
        <Alert onClose={() => closeSnackbar(key)} severity={severity}>
          {message}
        </Alert>
      ),
    })
  }

  const selectMeetingType = (newSelectedMeetingType: ConventionMeetingType) => {
    setSelectedMeetingType(newSelectedMeetingType)
    setScheduleMeetingStep(ScheduleMeetingSteps.SELECT_TIME)
  }

  const selectMeetingTime = (newSelectedMeetingTime: ConventionAvailableMeetingTime) => {
    setSelectedMeetingTime(newSelectedMeetingTime)
    setScheduleMeetingStep(ScheduleMeetingSteps.CONFIRM_DATE)
  }

  const scheduleMeeting = async () => {
    setIsLoading(true)

    try {
      if (!selectedMeetingTime) throw "selectedMeetingAvailability is null or undefined"
      if (!current) throw "current is undefined"

      const response = await postConventionMeetingRegistration({
        current,
        id: selectedMeetingTime.id,
        attributes: {
          convention_meeting_availability_id: selectedMeetingTime.meeting_availability_id,
          start_at: selectedMeetingTime.start_at.toISOString(),
          end_at: selectedMeetingTime.end_at.toISOString(),
        },
      })

      setLastSelectedMeetingId(response.id)
    } catch (error) {
      console.error(error)
    } finally {
      setScheduleMeetingStep(ScheduleMeetingSteps.REVIEW_DATE)
      setIsLoading(false)
    }
  }

  const cancelMeeting = async () => {
    setIsLoading(true)
    try {
      if (!lastSelectedMeetingId) throw "lastSelectedMeetingId is null or undefined"

      await deleteConventionMeetingRegistration({
        current: current!,
        id: lastSelectedMeetingId,
      })
      showSnackbar("Your meeting has been successfully cancelled.", "success")
    } catch (error) {
      console.error(error)
    } finally {
      setScheduleMeetingStep(ScheduleMeetingSteps.SELECT_TYPE)
      setSelectedMeetingType(null)
      setSelectedMeetingTime(null)
      setLastSelectedMeetingId(null)
      setIsLoading(false)
    }
  }

  const goToPreviousStep = () => {
    if (ScheduleMeetingSteps.SELECT_TIME) {
      setSelectedMeetingType(null)
      setSelectedMeetingTime(null)
      setScheduleMeetingStep(ScheduleMeetingSteps.SELECT_TYPE)
      return
    }
    if (ScheduleMeetingSteps.CONFIRM_DATE) {
      setSelectedMeetingTime(null)
      setScheduleMeetingStep(ScheduleMeetingSteps.SELECT_TIME)
      return
    }
  }

  return (
    <>
      <_Dialog open onClose={onClose}>
        {ScheduleMeetingSteps.SELECT_TYPE === scheduleMeetingStep && (
          <ScheduleMeetingDialogSelectType
            institutionName={institutionName}
            meetingTypes={meetingTypes}
            meetingAvailabilities={meetingAvailabilities}
            onSelectMeetingType={selectMeetingType}
            onClose={onClose}
          />
        )}
        {ScheduleMeetingSteps.SELECT_TIME === scheduleMeetingStep && (
          <ScheduleMeetingDialogSelectTime
            meetingType={selectedMeetingType!}
            meetingTimes={meetingTimes}
            onGoBack={goToPreviousStep}
            onContinue={selectMeetingTime}
            onClose={onClose}
          />
        )}
        {ScheduleMeetingSteps.CONFIRM_DATE === scheduleMeetingStep && (
          <ScheduleMeetingDialogConfirmDate
            selectedMeetingTime={selectedMeetingTime!}
            selectedMeetingType={selectedMeetingType!}
            isLoading={isLoading}
            onGoBack={goToPreviousStep}
            onClose={onClose}
            onBookMeeting={scheduleMeeting}
          />
        )}
        {ScheduleMeetingSteps.REVIEW_DATE === scheduleMeetingStep && (
          <ScheduleMeetingDialogReviewDate
            selectedMeetingTime={selectedMeetingTime!}
            selectedMeetingType={selectedMeetingType!}
            isLoading={isLoading}
            onClose={onClose}
            onCancel={cancelMeeting}
          />
        )}
      </_Dialog>
    </>
  )
}

export default ScheduleMeetingDialog

const _Dialog = styled(Dialog)(({ theme }) => ({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    display: "flex",
    flexDirection: "column",

    width: "100%",
    minWidth: 320,
    maxWidth: 410,
    maxHeight: 680,

    borderRadius: 10,
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 4px rgba(0, 0, 0, 0.12)",
    backgroundColor: theme.palette.gray.lightest,

    overflow: "hidden",

    [theme.breakpoints.up("sm")]: {
      maxWidth: 455,
    },
  },
})) as typeof Dialog

const getNearest5MinuteInterval = (number: number) => 5 - (number % 5)
